// ─── Les échéances rangées par ANNÉE — module PUR ────────────────
// Demandé par Cédric le 14/08 : une présentation en arbre, années puis mois,
// avec pour chaque niveau l'état prévu / réel / écart.
//
// Pourquoi grouper : une prévision mensuelle sans fin produit une liste qui
// s'allonge indéfiniment. Au bout de deux ans, on fait défiler vingt-quatre
// lignes pour trouver un mois — alors que l'année est le repère naturel.
//
// ⚠️ Les totaux d'une année n'agrègent QUE les mois qu'elle contient, sans
// remonter dans les précédentes : un total d'année qui inclurait l'historique
// entier ne serait plus un total d'année.

const cent = (n) => Math.round((Number(n) || 0) * 100) / 100;

// `occurrences` : celles de `historiqueLigne` (passées ou à venir).
// Renvoie les années DÉCROISSANTES — on regarde d'abord ce qui vient de se
// passer, pas ce qui date de trois ans.
export function grouperParAnnee(occurrences = []) {
  const par = new Map();
  for (const o of occurrences) {
    const annee = String(o?.mois || '').slice(0, 4);
    if (!/^\d{4}$/.test(annee)) continue;      // un mois illisible ne crée pas d'année
    if (!par.has(annee)) par.set(annee, []);
    par.get(annee).push(o);
  }
  return [...par.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([annee, mois]) => {
      const prevu = cent(mois.reduce((n, o) => n + Math.abs(o.prevu || 0), 0));
      const reel = cent(mois.reduce((n, o) => n + Math.abs(o.reel || 0), 0));
      return {
        annee,
        // Les mois d'une année restent CHRONOLOGIQUES : à l'intérieur d'une
        // année, on lit de janvier à décembre.
        mois: [...mois].sort((a, b) => String(a.mois).localeCompare(String(b.mois))),
        prevu,
        reel,
        // L'écart garde son signe : négatif = il manque, positif = il y a eu
        // plus que prévu. L'écran décide si c'est bon ou mauvais selon le sens
        // de la ligne — un dépassement sur un revenu est une bonne nouvelle.
        // ⚠️ Ne compte QUE les mois avec un réel constaté (17/08, Cédric,
        // capture à l'appui) — même règle que l'affichage PAR MOIS juste en
        // dessous (`ArbreEcheances.js`, gardé sur `reel > 0`). Un mois à venir
        // (réel nul) n'a rien à comparer ; l'inclure affichait en fait TOUT le
        // prévu de l'année, signé comme un excédent favorable — un an de
        // dépenses pas encore payées ressemblait à de l'argent en plus.
        ecart: cent(mois.reduce((n, o) => (
          Math.abs(o.reel || 0) > 0.005 ? n + (Math.abs(o.reel || 0) - Math.abs(o.prevu || 0)) : n
        ), 0)),
        nb: mois.length,
      };
    });
}

// L'année en cours doit s'ouvrir la première : c'est celle qu'on vient
// consulter. Renvoie la clé d'année, ou celle de l'année la plus récente si
// l'année courante n'est pas dans la liste.
export function anneeParDefaut(groupes = [], auj = null) {
  if (!groupes.length) return null;
  const courante = String(auj || new Date().toISOString().slice(0, 10)).slice(0, 4);
  return groupes.some(g => g.annee === courante) ? courante : groupes[0].annee;
}
